import AssignTasksToSelfButton from "@/components/Forms/SubForms/AssignTasksToSelfButton.tsx";
import BulkAssignTasksButton from "@/components/Forms/SubForms/BulkAssignTasksButton.tsx";
import MrtCaseTasks from "@/components/Table/MrtCaseTasks.tsx";
import { useCaseTask } from "@/contexts/CaseTasksProvider.tsx";
import type { CaseTask, CaseTaskWithDueDate } from "@/types/caseTask.ts";
import {
    Box,
    Grid2 as Grid,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";

const filteredTasksWithDueDates = (
    tasks: CaseTask[],
    dateStart: Dayjs | null,
    dateEnd: Dayjs | null,
    mode: string,
): CaseTaskWithDueDate[] => {
    const filteredTasks = tasks.filter((task) => {
        if (mode === "All") {
            return true;
        }

        return task.status === mode;
    });

    let filteredWithDueDate = filteredTasks.map((task) => {
        // For tasks with duration = 0, use a far future date for sorting purposes
        // This ensures they appear last and are never marked as overdue
        const dueDate =
            task.duration === 0
                ? new Date(9999, 11, 31) // Far future date for tasks without due dates
                : new Date(task.metaCreatedWhen.getTime() + task.duration * 24 * 60 * 60 * 1000);
        return {
            ...task,
            dueDate,
            overdue: task.duration === 0 ? false : dueDate < new Date(),
        };
    });

    if (dateStart && dateEnd) {
        filteredWithDueDate = filteredWithDueDate.filter((task) => {
            return (
                (dateStart.startOf("day").isBefore(task.dueDate) &&
                    dateEnd.endOf("day").isAfter(task.dueDate)) ||
                (dateStart.startOf("day").isBefore(task.metaCreatedWhen) &&
                    dateEnd.endOf("day").isAfter(task.metaCreatedWhen)) ||
                (dateStart.startOf("day").isBefore(task.dateCompleted) &&
                    dateEnd.endOf("day").isAfter(task.dateCompleted))
            );
        });
    }

    if (dateStart) {
        filteredWithDueDate = filteredWithDueDate.filter((task) => {
            return (
                dateStart.startOf("day").isBefore(task.dueDate) ||
                dateStart.startOf("day").isBefore(task.metaCreatedWhen) ||
                dateStart.startOf("day").isBefore(task.dateCompleted)
            );
        });
    }

    if (dateEnd) {
        filteredWithDueDate = filteredWithDueDate.filter((task) => {
            return (
                dateEnd.endOf("day").isAfter(task.dueDate) ||
                dateEnd.endOf("day").isAfter(task.metaCreatedWhen) ||
                dateEnd.startOf("day").isBefore(task.dateCompleted)
            );
        });
    }

    return filteredWithDueDate.sort((a, b) => {
        return a.dueDate.getTime() - b.dueDate.getTime();
    });
};

type Props = {
    foundCaseId: string;
    caseIdUUID: string;
};

const CaseTasksGroupByDate = ({ caseIdUUID, foundCaseId }: Props) => {
    const { caseTasksAll } = useCaseTask();
    const [mode, setMode] = useState<"Pending" | "Completed" | "All">("Pending");
    const [dateStart, setDateStart] = useState<Dayjs | null>(null);
    const [dateEnd, setDateEnd] = useState<Dayjs | null>(null);
    const [filteredTasks, setFilteredTasks] = useState<CaseTaskWithDueDate[] | null>(null);

    const dueDates = useMemo(() => {
        if (filteredTasks === null) {
            return [];
        }

        return [
            ...new Set(
                filteredTasks.map((task) =>
                    task.dueDate.toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                    }),
                ),
            ),
        ];
    }, [filteredTasks]);

    useEffect(() => {
        setFilteredTasks(filteredTasksWithDueDates(caseTasksAll, dateStart, dateEnd, mode));
    }, [caseTasksAll, mode, dateStart, dateEnd]);

    return (
        <Grid
            container
            sx={{
                borderRadius: "8px",
                p: 1,
            }}
        >
            <Grid size={12} sx={{ p: 1, mb: 1 }}>
                <ToggleButtonGroup
                    fullWidth
                    exclusive
                    value={mode}
                    color="primary"
                    size="small"
                    onChange={(_event, mode) => {
                        if (mode) {
                            setMode(mode);
                        }
                    }}
                    sx={{
                        mb: 2,
                    }}
                >
                    <ToggleButton value="Pending">Pending</ToggleButton>
                    <ToggleButton value="Completed">Complete</ToggleButton>
                    <ToggleButton value="All">All</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <Grid
                size={12}
                sx={{
                    mb: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography variant={"h5"}>Tasks</Typography>
                <Stack
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <BulkAssignTasksButton caseIdUUID={caseIdUUID} foundCaseId={foundCaseId} />
                    <AssignTasksToSelfButton foundCaseId={foundCaseId} caseIdUUID={caseIdUUID} />
                </Stack>
            </Grid>
            <Grid
                size={12}
                sx={{
                    my: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <DatePicker
                    label="Start Date"
                    value={dateStart}
                    onChange={(newValue) => setDateStart(newValue)}
                    sx={{
                        mr: 1,
                        width: "150px",
                    }}
                />
                <DatePicker
                    label="End Date"
                    value={dateEnd}
                    onChange={(newValue) => setDateEnd(newValue)}
                    sx={{
                        ml: 1,
                        width: "150px",
                    }}
                />
            </Grid>
            {mode === "Completed" && filteredTasks && (
                <MrtCaseTasks data={filteredTasks} showCompleteTaskColumn={false} />
            )}

            {mode !== "Completed" &&
                filteredTasks &&
                filteredTasks.filter((task) => task.overdue).length > 0 && (
                    <Grid key={"overdue"} sx={{ mb: 2, width: "100%" }}>
                        <Typography variant={"h6"} sx={{ fontWeight: 800, color: "red" }}>
                            Overdue
                        </Typography>
                        <Box sx={{ mb: 4 }}>
                            <MrtCaseTasks data={filteredTasks.filter((task) => task.overdue)} />
                        </Box>
                    </Grid>
                )}
            {mode !== "Completed" &&
                filteredTasks &&
                dueDates
                    .filter(
                        (dateString) =>
                            new Date(dateString).setHours(0, 0, 0, 0) >=
                            new Date().setHours(0, 0, 0, 0),
                    )
                    .map((dueDate) => {
                        let filteredTasksInDateRange: CaseTaskWithDueDate[] = filteredTasks;

                        if (dateStart && dateEnd) {
                            filteredTasksInDateRange = filteredTasks.filter((task) => {
                                return (
                                    (dateStart.startOf("day").isBefore(task.dueDate) &&
                                        dateEnd.endOf("day").isAfter(task.dueDate)) ||
                                    (dateStart.startOf("day").isBefore(task.metaCreatedWhen) &&
                                        dateEnd.endOf("day").isAfter(task.metaCreatedWhen))
                                );
                            });
                        }

                        if (dateStart) {
                            filteredTasksInDateRange = filteredTasks.filter((task) => {
                                return (
                                    dateStart.startOf("day").isBefore(task.dueDate) ||
                                    dateStart.startOf("day").isBefore(task.metaCreatedWhen)
                                );
                            });
                        }

                        if (dateEnd) {
                            filteredTasksInDateRange = filteredTasks.filter((task) => {
                                return (
                                    dateEnd.endOf("day").isAfter(task.dueDate) ||
                                    dateEnd.endOf("day").isAfter(task.metaCreatedWhen)
                                );
                            });
                        }

                        const tasks = filteredTasksInDateRange.filter((task) => {
                            return (
                                task.dueDate.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                }) === dueDate
                            );
                        });

                        if (tasks.length > 0) {
                            return (
                                <Grid key={dueDate} sx={{ mb: 2, width: "100%" }}>
                                    <Typography variant={"h6"}>
                                        {dueDate === "12/31/9999" ? "" : dueDate}
                                    </Typography>
                                    <Box sx={{ mb: 4 }}>
                                        <MrtCaseTasks data={tasks} />
                                    </Box>
                                </Grid>
                            );
                        }
                    })}
        </Grid>
    );
};

export default CaseTasksGroupByDate;
